<script lang="ts">
  import { onMount } from 'svelte';
  import { request, setAdminToggle, createPortalSession, reconcileSubscription, resumeSubscription, type FeatureToggles } from '../lib/api';

  let { workflow = 'sales' }: { workflow?: 'sales' | 'fund' } = $props();

  interface AdminUser {
    email: string;
    name: string;
    is_admin: boolean;
  }

  interface UsageSummary {
    month: string;
    user_count: number;
    outbound_eval: { used: number; free: number; remaining: number; billable: number; cost_per: number; total_cost: number };
    prospecting: { used: number; free: number; billable: number; cost_per: number; total_cost: number };
    voice: { used: number; free: number };
  }

  interface AdminData {
    users: AdminUser[];
    org_tier: number;
    org_active: boolean;
    org_days_remaining: number | null;
    subscription_ends_at?: string;
    toggles: FeatureToggles;
    pricing: { text: number; voice: number };
    usage?: UsageSummary;
  }

  let users = $state<AdminUser[]>([]);
  let orgTier = $state(0);
  let orgActive = $state(true);
  let orgDaysRemaining = $state<number | null>(null);
  let subscriptionEndsAt = $state<string>('');
  let loading = $state(true);
  let toast = $state('');
  let toggles = $state<FeatureToggles>({ outbound_eval: false, prospecting: false, call_attendance: false });
  let pricing = $state({ text: 150, voice: 250 });
  let usage = $state<UsageSummary | null>(null);

  function formatEndsAt(iso: string): string {
    if (!iso) return '';
    const d = new Date(iso + 'T00:00:00Z');
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
  }

  const tierLabels: Record<number, string> = {
    0: 'Trial',
    2: 'Base',
    3: 'Full',
  };

  onMount(async () => {
    try {
      const data = await request<AdminData>('/admin/users');
      users = data.users;
      orgTier = data.org_tier;
      orgActive = data.org_active;
      orgDaysRemaining = data.org_days_remaining;
      subscriptionEndsAt = data.subscription_ends_at ?? '';
      toggles = data.toggles ?? { outbound_eval: false, prospecting: false, call_attendance: false };
      pricing = data.pricing ?? { text: 150, voice: 250 };
      usage = data.usage ?? null;
    } catch (e) {
      toast = 'Failed to load users';
    }
    loading = false;
  });

  async function setOrgTier(tier: number) {
    try {
      await request('/admin/org/tier', {
        method: 'PUT',
        body: JSON.stringify({ tier }),
      });
      orgTier = tier;
      toast = `Organization set to ${tierLabels[tier]}`;
    } catch (e) {
      toast = 'Failed to update tier';
    }
    setTimeout(() => toast = '', 3000);
  }

  async function transferAdmin(email: string) {
    if (!confirm(`Transfer admin to ${email}? You will lose admin access.`)) return;
    try {
      await request(`/admin/users/${encodeURIComponent(email)}/admin`, {
        method: 'PUT',
        body: JSON.stringify({ is_admin: true }),
      });
      toast = `Admin transferred to ${email}. You no longer have admin access.`;
      // Redirect away from admin page after a moment
      setTimeout(() => window.location.reload(), 2000);
    } catch (e: any) {
      toast = e?.message || 'Failed to transfer admin';
    }
    setTimeout(() => toast = '', 5000);
  }

  async function deleteUser(email: string) {
    if (!confirm(`Delete ${email}? This cannot be undone.`)) return;
    try {
      await request(`/admin/users/${encodeURIComponent(email)}`, { method: 'DELETE' });
      users = users.filter(u => u.email !== email);
      toast = `${email} deleted`;
    } catch (e: any) {
      toast = e?.message || 'Failed to delete user';
    }
    setTimeout(() => toast = '', 3000);
  }

  let transferTarget = $state('');
  let inviteEmail = $state('');
  let inviting = $state(false);

  async function inviteUser() {
    if (!inviteEmail.trim()) return;
    inviting = true;
    try {
      await request('/admin/users/invite', {
        method: 'POST',
        body: JSON.stringify({ email: inviteEmail.trim() }),
      });
      toast = `Invite sent to ${inviteEmail}`;
      inviteEmail = '';
    } catch (e: any) {
      toast = e?.message || 'Failed to invite user';
    }
    inviting = false;
    setTimeout(() => toast = '', 3000);
  }

  import { currentView, userEmail } from '../lib/stores';
  import UpgradeModal from '../lib/UpgradeModal.svelte';

  let showUpgrade = $state(false);
  const isDev = $derived($userEmail.toLowerCase().endsWith('@dealton.ai'));

  async function handleToggle(feature: keyof FeatureToggles, newVal: boolean) {
    // If enabling on tier 0 → redirect to paywall/billing
    if (newVal && orgTier === 0) {
      toast = 'Upgrade from trial to enable paid features.';
      setTimeout(() => toast = '', 4000);
      showUpgrade = true;
      return;
    }
    // Outbound eval requires tier 2+ (Pro)
    if (newVal && feature === 'outbound_eval' && orgTier < 2) {
      toast = 'Upgrade to Pro to enable outbound evaluation.';
      setTimeout(() => toast = '', 4000);
      showUpgrade = true;
      return;
    }
    // Call attendance requires tier 3
    if (newVal && feature === 'call_attendance' && orgTier < 3) {
      toast = 'Upgrade to Voice to enable call attendance.';
      setTimeout(() => toast = '', 4000);
      showUpgrade = true;
      return;
    }
    // Turning off requires confirmation
    if (!newVal) {
      const labels: Record<string, string> = {
        outbound_eval: 'Outbound Evaluation',
        prospecting: 'Unlimited Prospecting',
        call_attendance: 'Call Attendance',
      };
      if (!confirm(`Disable ${labels[feature]}? Users will lose access to this feature.`)) return;
    }
    try {
      const res = await setAdminToggle(feature, newVal);
      toggles = res.toggles;
      toast = `${feature.replace(/_/g, ' ')} ${newVal ? 'enabled' : 'disabled'}`;
    } catch (e: any) {
      if (e?.status === 402) {
        showUpgrade = true;
      } else {
        toast = e?.message || 'Failed to update toggle';
      }
    }
    setTimeout(() => toast = '', 3000);
  }
</script>

<div class="page-title">Admin</div>
<p class="page-subtitle">{workflow === 'fund' ? 'Manage your firm’s team.' : 'Manage users, tiers, and billing for your organization.'}</p>

{#if loading}
  <p class="text-dim">Loading...</p>
{:else}

{#if workflow !== 'fund'}
<div class="card" style="margin-top:16px;">
  <h3 style="margin-bottom:12px;">Organization Plan</h3>
  <div style="display:flex; align-items:center; gap:12px; margin-bottom:8px; flex-wrap:wrap;">
    <span style="font-weight:600;">{tierLabels[orgTier] ?? `Tier ${orgTier}`}</span>
    {#if orgTier === 2}
      <span class="text-dim" style="font-size:12px;">${pricing.text}/mo per seat</span>
    {:else if orgTier === 3}
      <span class="text-dim" style="font-size:12px;">${pricing.voice}/mo per seat</span>
    {/if}
    {#if orgActive}
      <span class="badge-green">Active</span>
    {:else}
      <span class="badge-red">Expired</span>
    {/if}
    {#if orgTier === 0 && orgDaysRemaining !== null}
      <span class="text-dim" style="font-size:12px;">{orgDaysRemaining}d left</span>
    {:else if orgTier >= 2 && subscriptionEndsAt}
      <span class="text-dim" style="font-size:12px;">ends {formatEndsAt(subscriptionEndsAt)}</span>
    {/if}

    <div style="margin-left:auto; display:flex; gap:8px; align-items:center;">
      {#if orgTier === 0}
        <button onclick={() => showUpgrade = true} style="font-size:12px; font-weight:600;">
          Upgrade
        </button>
      {:else if orgTier >= 2 && subscriptionEndsAt}
        <button onclick={async () => {
          try {
            const r = await resumeSubscription();
            subscriptionEndsAt = r.ends_at ?? '';
            toast = 'Subscription resumed. It will renew normally at the next billing cycle.';
          } catch (e: any) {
            toast = e?.message || 'Could not resume subscription';
          }
          setTimeout(() => toast = '', 5000);
        }} style="font-size:12px; font-weight:600;">
          Resume Subscription
        </button>
      {:else if orgTier >= 2}
        <button onclick={async () => {
          try {
            const { url } = await createPortalSession();
            window.open(url, '_blank');
          } catch {
            toast = 'No billing account on file. Contact support if you believe this is an error.';
            setTimeout(() => toast = '', 5000);
          }
        }} style="font-size:12px;">
          Manage Billing
        </button>
      {/if}
    </div>
  </div>

  {#if isDev}
    <div style="margin-top:12px; padding-top:12px; border-top:1px solid var(--border); display:flex; align-items:center; gap:12px; flex-wrap:wrap;">
      <span class="text-dim" style="font-size:11px; text-transform:uppercase; letter-spacing:0.05em;">Dev override</span>
      <select value={orgTier} onchange={(e) => setOrgTier(parseInt((e.target as HTMLSelectElement).value))} style="font-size:12px;">
        <option value={0}>Trial</option>
        <option value={2}>Base</option>
        <option value={3}>Full</option>
      </select>
      <button onclick={async () => {
        try {
          const r = await reconcileSubscription();
          const dropped = r.cancelled_duplicates?.length
            ? ` (cancelled ${r.cancelled_duplicates.length} duplicate sub${r.cancelled_duplicates.length === 1 ? '' : 's'})`
            : '';
          if (!r.ok) {
            toast = `Reconcile: ${r.reason}${r.price_id ? ` (price ${r.price_id})` : ''}${dropped}`;
          } else if (r.before_tier === r.after_tier) {
            toast = `Tier already in sync (${tierLabels[r.after_tier] ?? r.after_tier}).${dropped}`;
          } else {
            orgTier = r.after_tier;
            toast = `Tier reconciled: ${tierLabels[r.before_tier] ?? r.before_tier} → ${tierLabels[r.after_tier] ?? r.after_tier}${dropped}`;
          }
        } catch (e: any) {
          toast = e?.message || 'Reconcile failed';
        }
        setTimeout(() => toast = '', 6000);
      }} style="font-size:12px;">
        Reconcile from Stripe
      </button>
      <span class="text-dim" style="font-size:11px;">Bypasses Stripe / pulls live state — for @dealton.ai testing only.</span>
    </div>
  {/if}
</div>
{/if}

<div class="card" style="margin-top:16px;">
  <h3 style="margin-bottom:12px;">Users ({users.length})</h3>
  <table class="admin-table">
    <thead>
      <tr>
        <th>Email</th>
        <th>Name</th>
        <th>Role</th>
        <th>Actions</th>
      </tr>
    </thead>
    <tbody>
      {#each users as u}
        <tr>
          <td>{u.email}</td>
          <td>{u.name || '—'}</td>
          <td>
            {#if u.is_admin}
              <span class="badge-admin">Admin</span>
            {:else}
              <span class="text-dim">Member</span>
            {/if}
          </td>
          <td>
            {#if !u.is_admin}
              <button class="btn-delete" onclick={() => deleteUser(u.email)}>Remove</button>
            {/if}
          </td>
        </tr>
      {/each}
    </tbody>
  </table>
</div>

{#if users.filter(u => !u.is_admin).length > 0}
<div class="card" style="margin-top:16px;">
  <h3 style="margin-bottom:12px;">Transfer Admin</h3>
  <p class="text-dim" style="font-size:12px; margin-bottom:8px;">Hand over admin access to another user. You will lose admin privileges.</p>
  <div style="display:flex; gap:8px; align-items:center;">
    <select bind:value={transferTarget} style="flex:1;">
      <option value="">Select user...</option>
      {#each users.filter(u => !u.is_admin) as u}
        <option value={u.email}>{u.name || u.email}</option>
      {/each}
    </select>
    <button class="btn-delete" onclick={() => { if (transferTarget) transferAdmin(transferTarget); }} disabled={!transferTarget}>
      Transfer
    </button>
  </div>
</div>
{/if}

<div class="card" style="margin-top:16px;">
  <h3 style="margin-bottom:12px;">Invite User</h3>
  <div style="display:flex; gap:8px; align-items:center;">
    <input bind:value={inviteEmail} placeholder="email@company.com" style="flex:1;" onkeydown={(e) => { if (e.key === 'Enter') inviteUser(); }} />
    <button onclick={inviteUser} disabled={inviting || !inviteEmail.trim()}>
      {inviting ? 'Sending...' : 'Send Invite'}
    </button>
  </div>
</div>

{#if workflow !== 'fund'}
<div class="card" style="margin-top:24px;">
  <h3 style="margin-bottom:12px;">Extended Features</h3>
  <p class="text-dim" style="font-size:12px; margin-bottom:12px;">Toggle to enable/disable for your team.</p>

  <div class="toggle-row">
    <div class="toggle-info">
      <span class="toggle-label">Outbound Evaluation</span>
      <span class="toggle-desc">Score and prioritize outbound-only prospects — first 50/user/month free</span>
      <span class="toggle-price">$0.05 / outbound, capped at $50/user/month</span>
    </div>
    <label class="toggle-switch">
      <input type="checkbox" checked={toggles.outbound_eval} onchange={() => handleToggle('outbound_eval', !toggles.outbound_eval)} />
      <span class="toggle-slider"></span>
    </label>
  </div>

  <div class="toggle-row">
    <div class="toggle-info">
      <span class="toggle-label">Unlimited Prospecting</span>
      <span class="toggle-desc">Research leads and generate personalized outreach emails — first 20/user/month free</span>
      <span class="toggle-price">$0.20 / lead, capped at $100/user/month</span>
    </div>
    <label class="toggle-switch">
      <input type="checkbox" checked={toggles.prospecting} onchange={() => handleToggle('prospecting', !toggles.prospecting)} />
      <span class="toggle-slider"></span>
    </label>
  </div>

  <div class="toggle-row" style="border-bottom:none;">
    <div class="toggle-info">
      <span class="toggle-label">Unlimited Call Attendance</span>
      <span class="toggle-desc">AI partner joins your calls to listen, take notes, and assist in real time</span>
      <span class="toggle-price">Included with Voice tier</span>
      {#if orgTier < 3}
        <span class="toggle-hint">Requires Voice tier</span>
      {/if}
    </div>
    <label class="toggle-switch">
      <input type="checkbox" checked={toggles.call_attendance} onchange={() => handleToggle('call_attendance', !toggles.call_attendance)} />
      <span class="toggle-slider"></span>
    </label>
  </div>

</div>
{/if}

{#if workflow !== 'fund'}
<div class="card" style="margin-top:32px; border-color: rgba(239, 68, 68, 0.2);">
  <h3 style="margin-bottom:8px; color: #ef4444;">Danger Zone</h3>
  <p class="text-dim" style="font-size:12px; margin-bottom:12px;">Permanently delete this organization and all its data. This cannot be undone.</p>
  <button class="btn-delete" onclick={async () => {
    const confirmText = prompt('Type "DELETE" to confirm organization deletion:');
    if (confirmText !== 'DELETE') return;
    try {
      await request('/admin/users/self', { method: 'DELETE' });
      toast = 'Organization deleted. Logging out...';
      setTimeout(() => window.location.reload(), 2000);
    } catch (e: any) {
      toast = e?.message || 'Failed to delete organization';
    }
  }}>
    Delete Organization
  </button>
</div>
{/if}

{/if}

{#if toast}
  <div class="toast">{toast}</div>
{/if}

{#if workflow !== 'fund'}<UpgradeModal show={showUpgrade} onClose={() => showUpgrade = false} />{/if}

<style>
  .admin-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }
  .admin-table th {
    text-align: left;
    padding: 8px 12px;
    border-bottom: 1px solid var(--border);
    color: var(--text-muted);
    font-weight: 500;
    font-size: 11px;
    text-transform: uppercase;
  }
  .admin-table td {
    height: var(--row-h);
    padding: 8px 12px;
    border-bottom: 1px solid var(--border);
    color: var(--text);
  }
  .admin-table select {
    font-size: 12px;
    /* Right padding clears the app-wide select arrow (app.css). */
    padding: 3px 22px 3px 8px;
    border-radius: var(--radius);
    border: 1px solid var(--border);
    background-color: var(--bg);   /* NOT `background:` — wipes the select arrow */
    color: var(--text);
  }
  /* NB: `.admin-table select` matches nothing — this page's two selects live outside
     that table, and svelte-check has flagged the rule as unused since before today. No
     `@supports` companion for it, then: adding one just doubles the warning. Those two
     selects take the global rules in app.css, base-select block included. */
  .badge-green {
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 10px;
    background: rgba(34, 197, 94, 0.15);
    color: #22c55e;
  }
  .badge-red {
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 10px;
    background: rgba(239, 68, 68, 0.15);
    color: #ef4444;
  }
  .badge-admin {
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 10px;
    background: rgba(59, 130, 246, 0.15);
    color: #3b82f6;
    font-weight: 600;
  }
  .btn-delete {
    font-size: 11px;
    padding: 2px 8px;
    border-radius: var(--radius);
    border: 1px solid rgba(239, 68, 68, 0.3);
    background: transparent;
    color: #ef4444;
    cursor: pointer;
  }
  .btn-delete:hover { background: rgba(239, 68, 68, 0.1); }
  .toggle-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 0;
    border-bottom: 1px solid var(--border);
  }
  .toggle-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .toggle-label {
    font-size: 13px;
    font-weight: 500;
    color: var(--text);
  }
  .toggle-desc {
    font-size: 11px;
    color: var(--text-muted);
  }
  .toggle-price {
    font-size: 11px;
    color: var(--text-dim);
  }
  .toggle-hint {
    font-size: 10px;
    color: var(--text-dim);
    font-style: italic;
  }
  /* The toggle is styled by app.css (.toggle-switch / .toggle-slider) — do not
     re-add a scoped copy. A hardcoded checked-state colour here would be
     theme-blind; ON must stay var(--accent), matching Settings and Onboarding. */
  .toggle-switch:has(input:disabled) {
    cursor: not-allowed;
  }
  .toast {
    position: fixed;
    bottom: 20px;
    right: 20px;
    background: var(--bg-surface);
    border: 1px solid var(--border);
    padding: 10px 20px;
    border-radius: var(--radius);
    font-size: 13px;
    color: var(--text);
    z-index: 999;
  }
</style>
